import React, { ChangeEvent, ReactElement, useEffect, useRef, useState } from "react";
import { Dropdown, Item, Menu, Separator } from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input} from "@zendeskgarden/react-forms";

import { IFormulaBarUpdates } from "../../../../../../contexts/account/data/useFormulaBarUpdates";

import * as css from "./styles/css.module.scss";
import { DistributionMethod, PercentGrowthBasePeriod, VersionType } from "../../../../../../__generated__/generated_types";
import { TooltipModal } from "@zendeskgarden/react-modals";
import { distributionOptions, getBaseToGrowOptions, stringToFixedFloat } from "../../logic/utils";
import { useProperties } from "../../../../../../contexts/properties/PropertiesContext";


const headerText = {
    baseToGrow: "Select base to grow",
    inputGrowthRate: "Input growth rate",
}

interface IGrowthMenuProps {
    fbUpdates: IFormulaBarUpdates,
    handleCloseParent?: () => void,
    level: number,
    triggerNode?: ReactElement,
    versionType: VersionType.Budget | VersionType.Reforecast,
}

export default function PercentGrowthMenu({
    fbUpdates,
    handleCloseParent,
    level,
    triggerNode,
    versionType,
}: IGrowthMenuProps): React.ReactElement {
    const { currentProperty } = useProperties();
    const [inputGrowthRate, setInputGrowthRate] = useState<string>("0");

    const triggerRef = useRef<HTMLDivElement>(null);
    const [refElement, setRefElement] = useState<HTMLDivElement|null>(null);

    useEffect(() => {
        const inputGrowthRateFromFbUpdates = fbUpdates.parsedFormDrivers.percentGrowth[0]?.percentGrowthAdjustmentValue || 0;
        setInputGrowthRate(inputGrowthRateFromFbUpdates.toString());
    }, [fbUpdates.parsedFormDrivers.percentGrowth[0]?.percentGrowthBasePeriod]);

    const handleSelectBaseToGrow = (item: PercentGrowthBasePeriod) => {
        fbUpdates.updatePercentGrowthDriverBaseToGrow(item);
        fbUpdates.updatePercentGrowthDriverRate(0);
        fbUpdates.updatePercentGrowthDriverDistribution(DistributionMethod.Flat);
        handleClose();
        if (handleCloseParent) handleCloseParent();
    };

    const handleSelectDistribution = (item: DistributionMethod) => {
        fbUpdates.updatePercentGrowthDriverDistribution(item);
        if (handleCloseParent) handleCloseParent();
        handleClose();
    };

    const handleInputGrowthRate = (event: ChangeEvent<HTMLInputElement>) => {
        setInputGrowthRate(event.target.value);
    };

    const handleCloseGrowthRate = () => {
        if (inputGrowthRate === '-' || inputGrowthRate.length === 0 || parseFloat(inputGrowthRate) < -100 || parseFloat(inputGrowthRate) > 100) {
            fbUpdates.updatePercentGrowthDriverRate(0);
            setInputGrowthRate("0");
        } else {
            fbUpdates.updatePercentGrowthDriverRate(stringToFixedFloat(inputGrowthRate, 2, false));
        }
        handleClose();
    };

    const handleInputGrowthRateSubmit = (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (event.key === 'Enter' && triggerNode !== undefined) {
            handleCloseGrowthRate();
        }
    };

    // The header is a non-clickable, static text separated from the menu body
    const renderMenuHeader = (text: string) => (
        <>
            <div
                style={inlineStyles.header}
            >
                {text}
            </div>
            <Separator style={inlineStyles.separator}/>
        </>
    );

    const handleClick = () => {
        setRefElement(triggerRef.current);
    };

    const handleClose = () => {
        setRefElement(null);
    };

    if (!currentProperty) {
        return <></>;
    }

    let baseToGrowOptions = getBaseToGrowOptions(versionType, currentProperty.reforecastYear);
    if (currentProperty.isAcquisitionProperty) {
        baseToGrowOptions = [
            {
                value: PercentGrowthBasePeriod.SameMonthLastYear,
                label: "Same month last year",
            },
            {
                value: PercentGrowthBasePeriod.LastYearActuals,
                label:  "T12 Total",
            },
        ];
    }

    const baseToGrowNode = (
        <Dropdown
            isOpen
            onSelect={(item) => handleSelectBaseToGrow(item)}
            downshiftProps={{ defaultHighlightedIndex: 0 }}
        >
            <div className={css.menuWrapper}>
                {renderMenuHeader(headerText.baseToGrow)}
                <Menu placement="bottom" className={css.menu} style={inlineStyles.menu}>
                    {baseToGrowOptions.map((each) => (
                        <Item
                            value={each.value}
                            key={each.value}
                            className={css.menuItem}
                        >
                            {each.label}
                        </Item>
                    ))}
                </Menu>
            </div>
        </Dropdown>
    );

    const inputGrowthRateNode = (
        <div className={css.menuWrapper}>
            {renderMenuHeader(headerText.inputGrowthRate)}
            <div className={`${css.inputWrapper} ${css.pseudoPercent}`}>
                <FormField>
                    <Input
                        value={inputGrowthRate === "0" ? undefined : inputGrowthRate}
                        type="number"
                        autoFocus
                        onChange={handleInputGrowthRate}
                        onKeyDown={handleInputGrowthRateSubmit}
                        style={inlineStyles.input}
                    />
                </FormField>
            </div>
        </div>
    );

    const distributionNode = (
        <Dropdown
            isOpen
            onSelect={(item) => handleSelectDistribution(item)}
            downshiftProps={{ defaultHighlightedIndex: 0 }}
        >
            <div className={css.menuWrapper}>
                <Menu placement="bottom" className={css.menu} style={inlineStyles.menu}>
                    {distributionOptions.map((each) => (
                        <Item
                            value={each.name}
                            key={each.name}
                            className={css.menuItem}
                        >
                            {each.value}
                        </Item>
                    ))}
                </Menu>
            </div>
        </Dropdown>
    );

    if (level === 0) {
        if (triggerNode) {
            return (
                <>
                    <div
                        className={css.triggerNode}
                        ref={triggerRef}
                        onClick={handleClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                        className={css.tooltipModal}
                        hasArrow={false}
                        referenceElement={refElement}
                        onClose={handleClose}
                        placement="bottom"
                        isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {baseToGrowNode}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
            );
        }
        return baseToGrowNode;
    }

    if (level === 1) {
        if (triggerNode) {
            return (
                <>
                    <div
                        className={css.triggerNode}
                        ref={triggerRef}
                        onClick={handleClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                        className={css.tooltipModal}
                        hasArrow={false}
                        focusOnMount={false}
                        referenceElement={refElement}
                        onClose={handleCloseGrowthRate}
                        placement="bottom"
                        isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {inputGrowthRateNode}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
            );
        }
        return inputGrowthRateNode;
    }

    if (level === 2) {
        if (triggerNode) {
            return (
                <>
                    <div
                        className={css.triggerNode}
                        ref={triggerRef}
                        onClick={handleClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                        className={css.tooltipModal}
                        hasArrow={false}
                        referenceElement={refElement}
                        onClose={handleClose}
                        placement="bottom"
                        isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {distributionNode}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
            );
        }
        return distributionNode;
    }

    return <></>;
}

/**
 * To override ZDG styles and avoid using !important all the time
 */
const inlineStyles = ({
    header: {color: "#2f3941", fontWeight: 500, margin: "0", padding: "12px"},
    input: {margin: "16px", minHeight: "39px", paddingLeft: "26px", width: "150px"},
    item: (depth: number) => ({paddingLeft: `${36 + depth * 24}px`}),
    menu: {marginTop: "30px", position: "relative" as 'relative', transform: "none", width: "auto"},
    separator: {margin: "0"},
});
